import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PaymentProvider = "paypal" | "razorpay";

export function usePaymentProvider() {
  const [provider, setProvider] = useState<PaymentProvider>("paypal");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPaymentProvider();
  }, []);

  const fetchPaymentProvider = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // No user logged in, default to paypal
        setProvider("paypal");
        setIsLoading(false);
        return;
      }

      // Check if user has an existing subscription
      const { data: subscription } = await supabase
        .from("subscriptions")
        .select("payment_provider, plan, provider_subscription_id")
        .eq("user_id", user.id)
        .maybeSingle();

      // Only use subscription's payment_provider if user has an ACTIVE paid subscription
      // (indicated by having a non-free plan AND a valid provider_subscription_id)
      const hasActivePaidSubscription = 
        subscription?.plan && 
        subscription.plan !== 'free' && 
        subscription.provider_subscription_id;

      if (hasActivePaidSubscription && 
          (subscription?.payment_provider === "razorpay" || subscription?.payment_provider === "paypal")) {
        setProvider(subscription.payment_provider as PaymentProvider);
        setIsLoading(false);
        return;
      }

      // For free users or new users, determine provider based on country
      const { data: profile } = await supabase
        .from("profiles")
        .select("country")
        .eq("id", user.id)
        .maybeSingle();

      // India = Razorpay, everyone else = PayPal
      if (profile?.country?.toLowerCase() === "india") {
        setProvider("razorpay");
      } else {
        setProvider("paypal");
      }
    } catch (error) {
      console.error("Error fetching payment provider:", error);
      // Default to PayPal on error
      setProvider("paypal");
    } finally {
      setIsLoading(false);
    }
  };

  return { provider, isLoading };
}
